import type { EntityId, MatchState, PlayerId, Squad } from '@signalclash/shared';

// Hit radii for desktop (1-px cursor) pickers. Touch path uses ~2× these so a
// finger contact patch (~40–60 CSS-px radius) can reliably hit individual
// targets. Kept here so InputRouter and any future ability resolver share one
// source of truth.
export const SQUAD_HIT_RADIUS = 24;
export const CORE_HIT_RADIUS = 80;
export const BUILDING_HIT_RADIUS = 56;
export const CONTROL_NODE_HIT_RADIUS = 56;
export const SQUAD_HIT_RADIUS_TOUCH = 34;
export const CORE_HIT_RADIUS_TOUCH = 60;
export const BUILDING_HIT_RADIUS_TOUCH = 56;
// Relays are large static landmarks the player frequently retargets (capture
// routes, Cut Link, defending under attack). A slightly larger radius than
// turrets/hubs makes the most-tapped target the most-forgiving.
export const CONTROL_NODE_HIT_RADIUS_TOUCH = 68;

export function pickSquad(
  state: MatchState,
  wx: number,
  wy: number,
  owner: PlayerId,
  radius: number = SQUAD_HIT_RADIUS,
): EntityId | null {
  let bestId: EntityId | null = null;
  let bestDist = radius;
  for (const s of Object.values(state.squads)) {
    if (s.ownerId !== owner) continue;
    const d = Math.hypot(s.pos.x - wx, s.pos.y - wy);
    if (d < bestDist) {
      bestDist = d;
      bestId = s.id;
    }
  }
  return bestId;
}

// Symmetric to pickSquad but for enemies — used by Mark Target click resolution.
export function pickEnemySquad(
  state: MatchState,
  wx: number,
  wy: number,
  owner: PlayerId,
  radius: number = SQUAD_HIT_RADIUS,
): EntityId | null {
  let bestId: EntityId | null = null;
  let bestDist = radius * 1.5;
  for (const s of Object.values(state.squads) as Squad[]) {
    if (s.ownerId === owner) continue;
    const d = Math.hypot(s.pos.x - wx, s.pos.y - wy);
    if (d < bestDist) {
      bestDist = d;
      bestId = s.id;
    }
  }
  return bestId;
}

export function pickCore(
  state: MatchState,
  wx: number,
  wy: number,
  owner: PlayerId,
  radius: number = CORE_HIT_RADIUS,
): EntityId | null {
  for (const c of Object.values(state.cores)) {
    if (c.ownerId !== owner) continue;
    const d = Math.hypot(c.pos.x - wx, c.pos.y - wy);
    if (d <= radius) return c.id;
  }
  return null;
}

export function pickBuilding(
  state: MatchState,
  wx: number,
  wy: number,
  owner: PlayerId,
  radius: number = BUILDING_HIT_RADIUS,
): EntityId | null {
  let bestId: EntityId | null = null;
  let bestDist = radius;
  for (const b of Object.values(state.buildings)) {
    if (b.ownerId !== owner) continue;
    const d = Math.hypot(b.pos.x - wx, b.pos.y - wy);
    if (d < bestDist) {
      bestDist = d;
      bestId = b.id;
    }
  }
  return bestId;
}

// V1.5+ Slice 2 — control_node picker for the Cut Link click resolver. Larger
// hit radius than squads (relays are big static targets and the player's
// cursor is on the relay, not on a 14-px squad). Owner-agnostic — any relay,
// including your own, can be cut (the sim re-checks range on apply).
export function pickControlNode(
  state: MatchState,
  wx: number,
  wy: number,
  radius: number = CONTROL_NODE_HIT_RADIUS,
): EntityId | null {
  let bestId: EntityId | null = null;
  let bestDist = radius;
  for (const node of Object.values(state.controlNodes)) {
    const d = Math.hypot(node.pos.x - wx, node.pos.y - wy);
    if (d < bestDist) {
      bestDist = d;
      bestId = node.id;
    }
  }
  return bestId;
}

export interface TouchTrack {
  id: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  startT: number;
  moved: boolean;
}

export type ArmedMode = 'idle' | 'mark-target' | 'cut-link' | 'build';
